import type { DemolitionFxController } from '../fallback/ImpactFx';
import type { PowderResidueStamp } from './PowderResidueLayer';
import type { CottageCanonicalEvent } from '../../structures/cottage/cottagePhysics';
import { demolitionPresentationSeed } from './demolitionPresentationDispatcher';

export interface CottagePresentationResidueSink { stamp(input: PowderResidueStamp): void }
export interface CottagePresentationDispatchOptions {
  readonly structureKey: string;
  readonly worldOffset?: { readonly x: number; readonly y: number; readonly z: number };
  readonly events: readonly CottageCanonicalEvent[];
  readonly effects?: DemolitionFxController | null;
  readonly residue?: CottagePresentationResidueSink | null;
  readonly effectsEnabled?: boolean;
}
export interface CottagePresentationDispatchTelemetry {
  readonly structureKey: string;
  readonly epoch: number | null;
  readonly eventsSeen: number;
  readonly impactBursts: number;
  readonly damageFacts: number;
  readonly powderClouds: number;
  readonly residueStamps: number;
  readonly pulverizedVolume: number;
  readonly effectsEnabled: boolean;
}

/** Route already-drained Cottage facts. Physical releases intentionally have no shard/trace FX. */
export function dispatchCottagePresentation(options: CottagePresentationDispatchOptions): CottagePresentationDispatchTelemetry {
  const enabled = options.effectsEnabled !== false;
  const effects = enabled ? options.effects ?? null : null;
  const residue = enabled ? options.residue ?? null : null;
  let firstEpoch: number | null = null;
  let mixedEpoch = false;
  let impactBursts = 0;
  let damageFacts = 0;
  let powderClouds = 0;
  let residueStamps = 0;
  let pulverizedVolume = 0;
  const offset = options.worldOffset ?? { x: 0, y: 0, z: 0 };
  for (const event of options.events) {
    if (firstEpoch === null) firstEpoch = event.epoch;
    else if (firstEpoch !== event.epoch) mixedEpoch = true;
    const seed = demolitionPresentationSeed(event.epoch, event.sequence);
    if (event.kind === 'impact-burst') {
      impactBursts += 1;
      effects?.spawnImpactBurst({
        ...event.payload,
        x: event.payload.x + offset.x,
        y: event.payload.y + offset.y,
        z: event.payload.z + offset.z,
        material: event.payload.material,
      }, seed);
      continue;
    }
    damageFacts += 1;
    // Cottage physical pieces own their full volume. Powder is eligible only
    // when the canonical fate explicitly allocates airborne fines; a stale
    // payload hint must never create duplicate visual material.
    const volume = event.fate.airborneFines.volume;
    if (!(volume > 1e-8)) continue;
    powderClouds += 1;
    pulverizedVolume += volume;
    const p = event.payload.position;
    const x = p.x + offset.x;
    const y = p.y + offset.y;
    const z = p.z + offset.z;
    effects?.spawnPulverizedCloud(x, y, z, event.payload.outwardNormal, volume, seed, event.material);
    residueStamps += 1;
    residue?.stamp({ x, z, volume, material: event.material, rest: [] });
  }
  return Object.freeze({ structureKey: options.structureKey, epoch: mixedEpoch ? null : firstEpoch, eventsSeen: options.events.length, impactBursts, damageFacts, powderClouds, residueStamps, pulverizedVolume, effectsEnabled: enabled });
}
